import React, { useEffect } from 'react';
import { TabCreationComboBoxRef } from '../../TabCreationComboBox';
import { PaneNode } from '../../../../types/pane.types';
import { findActiveTab, findContainerNode, getNextTabInContainer, getPreviousTabInContainer } from '../../../utils/paneUtils';

interface UseKeyboardShortcutsProps {
  tabCreationComboBoxRef: React.RefObject<TabCreationComboBoxRef | null>;
  layout: {
    rootPane: PaneNode | null;
    activeContainerId: string;
  };
  closeTab: (containerId: string, tabId: string) => void;
  activateTab: (containerId: string, tabId: string) => void;
}

/**
 * キーボードショートカットを管理するカスタムフック
 * - Cmd+P: コマンドパレットを開く
 * - Cmd+W: アクティブなタブを閉じる
 * - Cmd+] (macOS) / Ctrl+] (Windows/Linux): 次のタブへ切り替え
 * - Cmd+[ (macOS) / Ctrl+[ (Windows/Linux): 前のタブへ切り替え
 */
export function useKeyboardShortcuts({
  tabCreationComboBoxRef,
  layout,
  closeTab,
  activateTab
}: UseKeyboardShortcutsProps) {

  useEffect(() => {
    const handleKeyDown = async (event: KeyboardEvent) => {
      // Cmd+P (macOS) または Ctrl+P (Windows/Linux) でコマンドパレットを開く
      if ((event.metaKey || event.ctrlKey) && event.key === 'p') {
        event.preventDefault();

        if (tabCreationComboBoxRef.current) {
          tabCreationComboBoxRef.current.open();
        }
      }

      // Cmd+W (macOS) または Ctrl+W (Windows/Linux) でアクティブなタブを閉じる
      if ((event.metaKey || event.ctrlKey) && event.key === 'w') {
        event.preventDefault();

        // アクティブなタブを探す
        if (layout.rootPane && layout.activeContainerId) {
          const activeTab = findActiveTab(layout.rootPane, layout.activeContainerId);
          if (activeTab) {
            closeTab(layout.activeContainerId, activeTab.id);
          }
        }
      }

      // Cmd+] (macOS) または Ctrl+] (Windows/Linux) で次のタブへ切り替え
      if ((event.metaKey || event.ctrlKey) && event.key === ']') {
        event.preventDefault();

        if (layout.rootPane && layout.activeContainerId) {
          const container = findContainerNode(layout.rootPane, layout.activeContainerId);
          if (container) {
            const nextTabId = getNextTabInContainer(container, container.activeTabId);
            if (nextTabId) {
              activateTab(layout.activeContainerId, nextTabId);
            }
          }
        }
      }

      // Cmd+[ (macOS) または Ctrl+[ (Windows/Linux) で前のタブへ切り替え
      if ((event.metaKey || event.ctrlKey) && event.key === '[') {
        event.preventDefault();

        if (layout.rootPane && layout.activeContainerId) {
          const container = findContainerNode(layout.rootPane, layout.activeContainerId);
          if (container) {
            const previousTabId = getPreviousTabInContainer(container, container.activeTabId);
            if (previousTabId) {
              activateTab(layout.activeContainerId, previousTabId);
            }
          }
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [tabCreationComboBoxRef, layout, closeTab, activateTab]);
}